import React, { PropsWithChildren, useCallback, useRef, useState } from "react";
import { ModalProps, Image } from "react-bootstrap";

import "./ShareGameResultModal.scss";
import BottomSheet from "../../BottomSheet/BottomSheet";
import ImageCloseIcon from "../../../images/close-icon-purple.svg";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import FilledButton from "../../dashboard-page/FilledButton/FilledButton";
import DownloadIcon from "../../icons/DownloadIcon";
import ImageX from "../../../images/x-outlined-white.svg";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImageThreeDots from "../../../images/three-dots.svg";
import ImageHyper from "../../../images/hyper-banner-purple-bg.svg";
import { downloadPngImageFromDomContent } from "../../../utils/html-to-file";
import { getOsSafeCurrentDateTime } from "../../../utils/helpers";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import useNativeShare from "../../../hooks/useNativeShare";
import { POST_TO_X_LINK } from "../../../utils/constants/links";
import {
  GameOutcome,
  TOURNAMENT_TO_GAME_TYPE_MAP,
} from "../../../utils/constants";
import useAnalytics from "../../../hooks/useAnalytics";
import { Tournament } from "@nestcoinco/hyper-api-gateway-api-client";

const FILE_NAME_PREFIX = "hyper_result_";

function ShareGameResultModal({
  show,
  onHide,
  children,
  userOutcome,
  playerPosition,
  tournamentDetails,
}: PropsWithChildren<Pick<ModalProps, "show" | "onHide">> & {
  userOutcome?: GameOutcome;
  playerPosition: number | null;
  tournamentDetails?: Tournament;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const share = useNativeShare();
  const analytics = useAnalytics();
  const [isLoading] = useState(false);
  const [isDownloadingImg, setIsDownloadingImg] = useState(false);

  const handleDownloadResult = useCallback(async () => {
    if (nodeRef.current === null) return;
    const filename = `${FILE_NAME_PREFIX}${getOsSafeCurrentDateTime()}`;
    setIsDownloadingImg(true);
    await downloadPngImageFromDomContent(nodeRef.current, filename);
    setIsDownloadingImg(false);
  }, []);

  const sendTrackingEvent = useCallback(() => {
    if (!tournamentDetails) return;
    const { stakeAmount, type, game } = tournamentDetails;
    analytics.sendShareResultsEvent({
      entry_fee: stakeAmount,
      game_name: game.name,
      game_mode: TOURNAMENT_TO_GAME_TYPE_MAP[type],
      player_position: playerPosition,
    });
  }, [analytics, playerPosition, tournamentDetails]);

  const handleShare = useCallback(async () => {
    sendTrackingEvent();
    share({
      title: "Hyper Game Result",
      text: "Check out my Hyper game result!",
    });
  }, [share, sendTrackingEvent]);

  let postText = "It's a tie, go again? ⚒️";
  if (userOutcome === GameOutcome.WIN) postText = "*Mic drops! 🎮";
  if (userOutcome === GameOutcome.LOSS) postText = "So close. I'll be back! 💪🏽";

  return (
    <BottomSheet isOpen={!!show} className="pb-4 px-3">
      <div className="share-game-result-modal">
        <p className="py-2 d-flex justify-content-end px-0">
          <GhostButton
            aria-label="close"
            onClick={onHide}
            style={{ marginRight: -8 }}
          >
            <Image src={ImageCloseIcon} alt="close" width={30} />
          </GhostButton>
        </p>
        <div className="scrollable-container">
          <div ref={nodeRef} className="w-100">
            <div className="screenshot-container mb-3">
              {children}
              <p className="d-flex justify-content-center mt-3">
                <Image src={ImageHyper} alt="hyper logo" />
              </p>
            </div>
          </div>
        </div>

        <div className="colored-container d-flex justify-content-between align-items-center mb-3">
          <div>
            <p className="step mb-1">step 1:</p>
            <p className="instruction">download photo</p>
          </div>
          <GhostButton
            className="download-btn slashed-zero d-flex gap-2 px-3"
            onClick={handleDownloadResult}
          >
            <DownloadIcon />{" "}
            {isDownloadingImg ? (
              <CustomSpinner isPrimary size="sm" />
            ) : (
              <span>Download</span>
            )}
          </GhostButton>
        </div>

        <div className="colored-container">
          <p className="step mb-1">step 2</p>
          <p className="instruction mb-3">share photo to hyper community</p>
          <div className="d-flex gap-1">
            <PrimaryActionButton
              as="a"
              href={`${POST_TO_X_LINK}?text=${postText}`}
              target="_blank"
              rel="noreferrer"
              text={
                userOutcome === GameOutcome.WIN
                  ? "Brag in Hyper community"
                  : "Post to Hyper Community"
              }
              textClassName="share-btn-text"
              leftImage={ImageX}
              style={{
                background:
                  "linear-gradient(0deg, #333 0%, #333 100%), linear-gradient(180deg, #37BBFE 0%, #007DBB 100%), linear-gradient(227deg, #5BD066 9.27%, #27B43E 100%), #6C53E8",
                height: 40,
                borderRadius: 12,
                paddingTop: 8,
              }}
              onClick={sendTrackingEvent}
            />
            <FilledButton onClick={handleShare}>
              {isLoading ? (
                <CustomSpinner size="sm" />
              ) : (
                <Image src={ImageThreeDots} alt="" />
              )}
            </FilledButton>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}

export default ShareGameResultModal;
